'use client';
import React, { useState, forwardRef } from "react";
import { TextField, Card, CardContent, CardHeader, FormControl, InputLabel} from '@mui/material';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import PersonIcon from '@mui/icons-material/Person';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { IMaskInput } from 'react-imask';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers';
import { PersonalInfoErrors, PersonalInfoModel } from '@/app/Models/PersonalInfoModel';

const PhoneMaskInput = forwardRef<HTMLInputElement, { onChange: (e: { target: { value: string } }) => void }>(
    function PhoneMaskInput({ onChange, ...props }, ref) {
        return (
            <IMaskInput
                {...props}
                mask="(000) 000-0000"
                inputRef={ref}
                onAccept={(value) => onChange({ target: { value: value as string } })}
            />
        );
    }
);

interface Props {
    data: PersonalInfoModel;
    setPersonalData: (model: PersonalInfoModel) => void;
    errors: PersonalInfoErrors;
}

const PersonalCard: React.FC<Props> = (props) => {
    const [showPayoutIdField, setShowPayoutIdField] = useState(false);
    
    const handlePayoutMethodChange = (event: SelectChangeEvent<string>) => {
        const newValue = event.target.value;
        setShowPayoutIdField(newValue === "paypal" || newValue === "venmo");
        props.setPersonalData({...props.data, payoutMethod: newValue})
    }
    
    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Card sx={{ minWidth: 275}} variant="outlined">
                <CardHeader title='Personal Information'
                    avatar={
                        <PersonIcon />
                    }/>
                    
                <CardContent>
                    <TextField
                        required
                        tabIndex={1}
                        error={props.errors.firstNameError}
                        variant="standard"
                        value={props.data.firstName}
                        onChange={(e) => props.setPersonalData({...props.data, firstName: e.target.value})}
                        id="p-firstname-textfield"
                        label="First Name"
                    />
                    <TextField
                        variant="standard"
                        value={props.data.middleName}
                        onChange={(e) => props.setPersonalData({...props.data, middleName: e.target.value})}
                        id="p-middlename-textfield"
                        label="Middle Name" 
                    />
                    <TextField
                        required
                        error={props.errors.lastNameError}
                        variant="standard"
                        value={props.data.lastName}
                        onChange={(e) => props.setPersonalData({...props.data, lastName: e.target.value})}
                        id="p-lastname-textfield"
                        label="Last Name" 
                    />
                    <TextField
                        required
                        error={props.errors.emailError}
                        variant="standard"
                        value={props.data.email}
                        onChange={(e) => props.setPersonalData({...props.data, email: e.target.value})}
                        id="p-email-textfield"
                        label="Email"
                        helperText="Email will be used for communication on the status of your request."
                        inputProps={{ readOnly: true }}
                    />

                    <TextField
                        required
                        error={props.errors.phoneNumberError}
                        variant="standard"
                        id="p-phonenumber-textfield"
                        label="Phone Number"
                        value={props.data.phoneNumber ?? ''}
                        onChange={(e) => props.setPersonalData({...props.data, phoneNumber: e.target.value})}
                        InputProps={{ inputComponent: PhoneMaskInput as any }}
                    />

                    <DatePicker
                        value={props.data.dateOfBirth || null}
                        onChange={(date) => props.setPersonalData({...props.data, dateOfBirth: date})}
                        label="Date of Birth"
                    />

                    <FormControl sx={{ m: 1, minWidth: 150 }}>    
                        <InputLabel>Payout Method</InputLabel>
                        <Select
                            required
                            name="payoutMethod"
                            value={props.data.payoutMethod || ""}
                            onChange={handlePayoutMethodChange}
                            label="Payout Method"
                        >
                            <MenuItem value="check">Check</MenuItem>
                            <MenuItem value="paypal">PayPal</MenuItem>
                            <MenuItem value="venmo">Venmo</MenuItem>
                        </Select>
                    </FormControl>

                    {showPayoutIdField &&
                      <TextField
                          required
                          variant="standard"
                          value={props.data.payoutId}
                          onChange={(e) => props.setPersonalData({...props.data, payoutId: e.target.value})}
                          id="p-payoutId-username-textfield"
                          label="Payout Id"
                          helperText="If using Venmo please provide handle, else email address is needed for PayPal."
                      />
                    }
                </CardContent>
            </Card>
        </LocalizationProvider>
    );
};

export default PersonalCard;